import { Routes, Route, Navigate } from 'react-router-dom'
import { Suspense, useEffect, useRef, type ComponentType, type ReactNode } from 'react'
import { lazyWithRetry } from './utils/lazyWithRetry'
import { useDispatch } from 'react-redux'
import toast from 'react-hot-toast'
import { useAuth } from './hooks/useAuth'
import { setLoading, setCredentials, updateAccessToken, logout } from './store/slices/authSlice'
import { authService } from './services/authService'

// Pages
const lazyNamed = <TModule extends Record<string, unknown>>(
  importer: () => Promise<TModule>,
  exportName: keyof TModule
) => lazyWithRetry(async () => ({
  default: (await importer())[exportName] as ComponentType,
}))

const LoginPage = lazyWithRetry(() => import('./pages/auth/LoginPage'))
const RegisterPage = lazyWithRetry(() => import('./pages/auth/RegisterPage'))
const OAuthCallbackPage = lazyWithRetry(() => import('./pages/auth/OAuthCallbackPage'))
const ForgotPasswordPage = lazyNamed(() => import('./pages/auth/ForgotPasswordPage'), 'ForgotPasswordPage')
const ResetPasswordPage = lazyNamed(() => import('./pages/auth/ResetPasswordPage'), 'ResetPasswordPage')
const DashboardPage = lazyWithRetry(() => import('./pages/DashboardPage'))
const BrandsPage = lazyWithRetry(() => import('./pages/brands/BrandsPage'))
const UseCasesPage = lazyWithRetry(() => import('./pages/mocks/MocksPage'))
const MockEditorPage = lazyWithRetry(() => import('./pages/mocks/MockEditorPage'))
const TemplatesPage = lazyWithRetry(() => import('./pages/templates/TemplatesPage'))
const AdminPage = lazyWithRetry(() => import('./pages/admin/AdminPage'))
const MapKitSettingsPage = lazyWithRetry(() => import('./pages/admin/MapKitSettingsPage'))
const PlatformAdminDashboard = lazyNamed(() => import('./pages/platform-admin/PlatformAdminDashboardNew'), 'PlatformAdminDashboard')
const OrganizationsListPage = lazyNamed(() => import('./pages/platform-admin/OrganizationsListPage'), 'OrganizationsListPage')
const OrganizationDetailPage = lazyNamed(() => import('./pages/platform-admin/OrganizationDetailPage'), 'OrganizationDetailPage')
const UsersSearchPage = lazyNamed(() => import('./pages/platform-admin/UsersSearchPage'), 'UsersSearchPage')
const AIUsagePage = lazyNamed(() => import('./pages/platform-admin/AIUsagePage'), 'AIUsagePage')
const PlatformAuditLogPage = lazyWithRetry(() => import('./pages/platform-admin/AuditLogPage'))
const AnimationTuningPage = lazyNamed(() => import('./pages/platform-admin/AnimationTuningPage'), 'AnimationTuningPage')
const SessionSecurityPage = lazyNamed(() => import('./pages/platform-admin/SessionSecurityPage'), 'SessionSecurityPage')
const TenantAdminDashboard = lazyNamed(() => import('./pages/tenant-admin/TenantAdminDashboard'), 'TenantAdminDashboard')
const UserManagementPage = lazyWithRetry(() => import('./pages/tenant-admin/UserManagementPage'))
const OrgSettingsPage = lazyNamed(() => import('./pages/tenant-admin/OrgSettingsPage'), 'OrgSettingsPage')
const TenantAuditLogPage = lazyWithRetry(() => import('./pages/tenant-admin/AuditLogPage'))
const WebhooksPage = lazyWithRetry(() => import('./pages/tenant-admin/WebhooksPage'))
const QuotaPage = lazyWithRetry(() => import('./pages/tenant-admin/QuotaPage'))
const PlatformPromptsPage = lazyNamed(() => import('./pages/platform-admin/PromptsManagementPage'), 'PromptsManagementPage')
const TenantPromptsPage = lazyNamed(() => import('./pages/tenant-admin/PromptsManagementPage'), 'PromptsManagementPage')
const SharePage = lazyWithRetry(() => import('./pages/SharePage'))
const WalkthroughPage = lazyWithRetry(() => import('./pages/WalkthroughPage'))
const WalkthroughPublisherPage = lazyWithRetry(() => import('./pages/WalkthroughPublisherPage'))
const DealRoomPage = lazyWithRetry(() => import('./pages/DealRoomPage'))
const DealRoomBuilderPage = lazyWithRetry(() => import('./pages/sales/DealRoomBuilderPage'))
const PublishedAssetsPage = lazyWithRetry(() => import('./pages/PublishedAssetsPage'))
const SharedLinksPage = lazyWithRetry(() => import('./pages/SharedLinksPage'))
const SettingsPage = lazyWithRetry(() => import('./pages/settings/SettingsPage'))
const UserGuidePage = lazyWithRetry(() => import('./pages/UserGuidePage'))
const SharingDashboard = lazyWithRetry(() => import('./pages/collaboration/SharingDashboard'))
const RecycleBinPage = lazyWithRetry(() => import('./pages/RecycleBinPage'))
const SalesOverviewPage = lazyWithRetry(() => import('./pages/SalesOverviewPage'))
// Gated import: in production builds LEAD_QUALIFIER_DEV constant folds to false,
// so Vite/Rollup dead code eliminates the dynamic import and the chunk is never
// emitted. This makes the dev only exclusion a true bundle exclusion, not just
// an unreachable route. The fallback is never rendered (the route is gated too).
const LeadQualifierPage = LEAD_QUALIFIER_DEV
  ? lazyWithRetry(() => import('./pages/LeadQualifierPage'))
  : (() => null)
const GuidedStartPage = lazyWithRetry(() => import('./pages/mocks/GuidedStartPage'))
const IndustryDiscoveryPage = lazyWithRetry(() => import('./pages/IndustryDiscoveryPage'))
const DiscoveryPage = lazyWithRetry(() => import('./pages/DiscoveryPage'))
const AccountResearchPage = lazyWithRetry(() => import('./pages/AccountResearchPage'))
const UseCaseBuilderPage = lazyWithRetry(() => import('./pages/UseCaseBuilderPage'))
const FlowDesignerPage = lazyWithRetry(() => import('./pages/FlowDesignerPage'))
const ValueProjectionPage = lazyWithRetry(() => import('./pages/ValueProjectionPage'))
const TeamDashboardPage = lazyWithRetry(() => import('./pages/sales/TeamDashboardPage'))
const FigmaTemplatesPage = lazyWithRetry(() => import('./pages/FigmaTemplatesPage'))
const NotFoundPage = lazyWithRetry(() => import('./pages/NotFoundPage'))

// Layouts
import MainLayout from './components/layouts/MainLayout'
import AuthLayout from './components/layouts/AuthLayout'

// Route Guards
import { PlatformAdminRoute } from './components/routing/PlatformAdminRoute'
import { TenantAdminRoute } from './components/routing/TenantAdminRoute'
import { MapKitAdminRoute } from './components/routing/MapKitAdminRoute'
import { SalesEnablementRoute } from './components/routing/SalesEnablementRoute'
import { LEAD_QUALIFIER_DEV } from './utils/leadQualifierFlag'
import { GUIDED_STUDIO_ENABLED } from './utils/guidedStudioFlag'

// Protected Route Component
function RouteFallback() {
  return <div className="flex min-h-screen items-center justify-center text-sm text-gray-500">Loading...</div>
}

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()
  const dispatch = useDispatch()
  
  useEffect(() => {
    // Check if user is authenticated on mount
    const checkAuth = async () => {
      const token = localStorage.getItem('token')
      if (token) {
        try {
          // Verify token by getting current user (always refresh on mount to keep permissions current)
          const user = await authService.getCurrentUser()
          dispatch(setCredentials({
            user: {
              id: user.id,
              email: user.email,
              full_name: user.full_name,
              first_name: user.first_name,
              last_name: user.last_name,
              job_title: user.job_title,
              phone: user.phone,
              department: user.department,
              country: user.country,
              role: user.role as 'owner' | 'admin' | 'editor' | 'viewer',
              organization_id: user.organization_id,
              organization_name: user.organization_name,
              is_super_admin: user.is_super_admin,
              can_share_internally: user.can_share_internally,
              can_create_external_share_links: user.can_create_external_share_links,
              can_download_gif: user.can_download_gif,
              can_download_mp4: user.can_download_mp4,
              can_use_sales_enablement: user.can_use_sales_enablement,
              sales_enablement_enabled: user.sales_enablement_enabled,
              organization_sales_enablement_enabled: user.organization_sales_enablement_enabled,
            },
            // A request interceptor may have renewed an access token while
            // `/auth/me` was in flight. Never overwrite it with the stale token
            // captured before that request.
            token: localStorage.getItem('token') || token,
          }))
        } catch {
          // Token is invalid, clear it
          localStorage.removeItem('token')
          dispatch(logout())
        }
      }
      dispatch(setLoading(false))
    }
    
    checkAuth()
  }, [dispatch, isAuthenticated])
  
  if (isLoading) {
    return <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="text-lg mb-2">Loading...</div>
        <div className="text-sm text-gray-500">Connecting to backend...</div>
      </div>
    </div>
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }
  
  return <>{children}</>
}

function App() {
  const dispatch = useDispatch()
  const sessionToastShown = useRef(false)

  useEffect(() => {
    const handleSessionExpired = () => {
      if (sessionToastShown.current) return
      sessionToastShown.current = true
      dispatch(logout())
      toast.error('Your session expired. Please sign in again before continuing.', { duration: 9500 })
    }

    window.addEventListener('auth:session-expired', handleSessionExpired)
    const handleTokenRefreshed = (event: Event) => {
      const token = (event as CustomEvent<{ token?: string }>).detail?.token
      if (token) {
        sessionToastShown.current = false
        dispatch(updateAccessToken(token))
      }
    }
    window.addEventListener('auth:token-refreshed', handleTokenRefreshed)
    return () => {
      window.removeEventListener('auth:session-expired', handleSessionExpired)
      window.removeEventListener('auth:token-refreshed', handleTokenRefreshed)
    }
  }, [dispatch])

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === 'token' && !event.newValue) {
        window.dispatchEvent(new CustomEvent('auth:session-expired'))
      }
    }

    window.addEventListener('storage', handleStorage)
    return () => {
      window.removeEventListener('storage', handleStorage)
    }
  }, [])

  return (
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      {/* Public Share Route (no auth required) */}
      <Route path="/share/:token" element={<SharePage />} />

      {/* Public Deal Room Route (no auth required) */}
      <Route path="/deal/:token" element={<DealRoomPage />} />

      {/* Public Walkthrough Page Route (no auth required) */}
      <Route path="/walkthrough/:token" element={<WalkthroughPage />} />

      {/* Auth Routes */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/auth/callback" element={<OAuthCallbackPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
      </Route>

      {/* Protected Routes */}
      <Route element={
        <ProtectedRoute>
          <MainLayout />
        </ProtectedRoute>
      }>
        <Route path="/" element={<DashboardPage />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/brands" element={<BrandsPage />} />
        <Route path="/use-cases" element={<UseCasesPage />} />
        {GUIDED_STUDIO_ENABLED && (
          <Route path="/use-cases/guided-start" element={<GuidedStartPage />} />
        )}
        {/* A new use case has no mock id yet. Keep the old URL as a safe
            redirect instead of asking the editor to load mock id "new". */}
        <Route path="/use-cases/new" element={<Navigate to="/use-cases?create=1" replace />} />
        <Route path="/use-cases/:id" element={<MockEditorPage />} />
        <Route path="/templates" element={<TemplatesPage />} />
        <Route path="/tools/walkthrough" element={<WalkthroughPublisherPage />} />
        <Route path="/figma-templates" element={<FigmaTemplatesPage />} />
        <Route path="/sales-engagement" element={<SalesEnablementRoute><SalesOverviewPage /></SalesEnablementRoute>} />
        {LEAD_QUALIFIER_DEV && (
          <Route path="/sales-engagement/lead-qualifier" element={<SalesEnablementRoute><LeadQualifierPage /></SalesEnablementRoute>} />
        )}
        <Route path="/sales-engagement/account-research" element={<SalesEnablementRoute><AccountResearchPage /></SalesEnablementRoute>} />
        <Route path="/sales-engagement/industry-discovery" element={<SalesEnablementRoute><IndustryDiscoveryPage /></SalesEnablementRoute>} />
        <Route path="/sales-engagement/discovery" element={<SalesEnablementRoute><DiscoveryPage /></SalesEnablementRoute>} />
        <Route path="/sales-engagement/use-case-builder" element={<SalesEnablementRoute><UseCaseBuilderPage /></SalesEnablementRoute>} />
        <Route path="/sales-engagement/value-projection" element={<SalesEnablementRoute><ValueProjectionPage /></SalesEnablementRoute>} />
        <Route path="/sales-engagement/flow-designer" element={<SalesEnablementRoute><FlowDesignerPage /></SalesEnablementRoute>} />
        <Route path="/sales-engagement/deal-room" element={<SalesEnablementRoute><DealRoomBuilderPage /></SalesEnablementRoute>} />
        <Route path="/sales-engagement/team" element={<SalesEnablementRoute><TeamDashboardPage /></SalesEnablementRoute>} />
        <Route path="/collaboration" element={<SharingDashboard />} />
        <Route path="/published-assets" element={<PublishedAssetsPage />} />
        <Route path="/shared-links" element={<SharedLinksPage />} />
        <Route path="/recycle-bin" element={<RecycleBinPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/help" element={<UserGuidePage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/admin/mapkit" element={
          <MapKitAdminRoute>
            <MapKitSettingsPage />
          </MapKitAdminRoute>
        } />
        
        {/* Platform Admin Routes */}
        <Route path="/platform-admin" element={
          <PlatformAdminRoute>
            <PlatformAdminDashboard />
          </PlatformAdminRoute>
        } />
        <Route path="/platform-admin/organizations" element={
          <PlatformAdminRoute>
            <OrganizationsListPage />
          </PlatformAdminRoute>
        } />
        <Route path="/platform-admin/organizations/:id" element={
          <PlatformAdminRoute>
            <OrganizationDetailPage />
          </PlatformAdminRoute>
        } />
        <Route path="/platform-admin/users" element={
          <PlatformAdminRoute>
            <UsersSearchPage />
          </PlatformAdminRoute>
        } />
        <Route path="/platform-admin/prompts" element={
          <PlatformAdminRoute>
            <PlatformPromptsPage />
          </PlatformAdminRoute>
        } />
        <Route path="/platform-admin/ai-usage" element={
          <PlatformAdminRoute>
            <AIUsagePage />
          </PlatformAdminRoute>
        } />
        <Route path="/platform-admin/audit-log" element={
          <PlatformAdminRoute>
            <PlatformAuditLogPage />
          </PlatformAdminRoute>
        } />
        <Route path="/platform-admin/animation-tuning" element={
          <PlatformAdminRoute>
            <AnimationTuningPage />
          </PlatformAdminRoute>
        } />
        <Route path="/platform-admin/session-security" element={
          <PlatformAdminRoute>
            <SessionSecurityPage />
          </PlatformAdminRoute>
        } />

        {/* Tenant Admin Routes */}
        <Route path="/tenant-admin" element={
          <TenantAdminRoute>
            <TenantAdminDashboard />
          </TenantAdminRoute>
        } />
        <Route path="/tenant-admin/users" element={
          <TenantAdminRoute>
            <UserManagementPage />
          </TenantAdminRoute>
        } />
        <Route path="/tenant-admin/settings" element={
          <TenantAdminRoute>
            <OrgSettingsPage />
          </TenantAdminRoute>
        } />
        <Route path="/tenant-admin/audit-log" element={
          <TenantAdminRoute>
            <TenantAuditLogPage />
          </TenantAdminRoute>
        } />
        <Route path="/tenant-admin/webhooks" element={
          <TenantAdminRoute>
            <WebhooksPage />
          </TenantAdminRoute>
        } />
        <Route path="/tenant-admin/prompts" element={
          <TenantAdminRoute>
            <TenantPromptsPage />
          </TenantAdminRoute>
        } />
        <Route path="/tenant-admin/quota" element={
          <TenantAdminRoute>
            <QuotaPage />
          </TenantAdminRoute>
        } />
      </Route>

      {/* Fallback — show 404 for authenticated users, redirect to login otherwise */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </Suspense>
  )
}

export default App
